import { AddCircleOutline } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { currency, API_URL } from "const/constants";
import axios from "const/axios";
import { addMonths, format, parseISO } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { formatDisplayDate } from "utils/dateFormat";
import { isOshcInsuranceRow } from "./accountUtils";
import CommissionMonthRow from "./CommissionMonthRow";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import NotesModal from "./NotesModal";
import {
  useCommissionLedger,
  useCommissionMonthNotes,
  useCreateCommissionMonth,
  useDeleteCommissionMonth,
  useUpdateCommissionMonth,
} from "./insuranceCommissionApi";

const money = (value) => `${currency}${Number(value || 0).toFixed(2)}`;
const todayInput = () => format(new Date(), "yyyy-MM-dd");
const currentMonth = () => format(new Date(), "yyyy-MM");

const nextMonthAfter = (months) => {
  const latest = [...months].filter(Boolean).sort().at(-1);
  if (!latest) return currentMonth();
  try {
    return format(addMonths(parseISO(`${latest}-01`), 1), "yyyy-MM");
  } catch {
    return currentMonth();
  }
};

const draftKey = () => `draft-${crypto.randomUUID()}`;

const toEditableRow = (month) => ({
  key: String(month.id),
  id: month.id,
  month: month.month ?? month.forMonth ?? null,
  amount: month.amount ?? "",
  receivedDate: month.receivedDate || "",
  notesCount: month.notesCount || 0,
  dirty: false,
});

const validate = (row, siblings, { oneTime } = {}) => {
  const errors = {};
  const amount = Number(row.amount);
  if (!oneTime) {
    if (!row.month) errors.month = "Select the month this commission belongs to.";
    else if (siblings.some((other) => other.key !== row.key && other.month === row.month)) {
      errors.month = "This month is already recorded.";
    }
  }
  if (!row.amount || !Number.isFinite(amount) || amount <= 0) {
    errors.amount = "Enter an amount greater than 0.";
  }
  if (row.receivedDate && row.receivedDate > todayInput()) {
    errors.receivedDate = "Received date cannot be in the future.";
  }
  return errors;
};

/**
 * The received-commission ledger of one insurance account row. OVHC stays month-by-month; OSHC is
 * one lump with the same commission-months API shape (month/forMonth sent as null). Every entry is
 * written as it is confirmed so closing the dialog can never lose one; each entry carries its own
 * note thread, and deleting it takes those notes with it.
 */
const InsuranceCommissionModal = ({ open, onClose, row, onAccountChanged }) => {
  const accountDetailId = row?.id ?? null;
  const [linkedInsuranceType, setLinkedInsuranceType] = useState(null);
  const oneTime = isOshcInsuranceRow({
    ...row,
    type: linkedInsuranceType || row?.type || row?.insuranceType,
  });
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [notesTarget, setNotesTarget] = useState(null);

  const ledgerQuery = useCommissionLedger(accountDetailId, { enabled: open });
  const ledger = ledgerQuery.data;
  const locked = Boolean(ledger?.commissionCompleted);

  const notesQuery = useCommissionMonthNotes(notesTarget?.id, { enabled: Boolean(notesTarget) });

  useEffect(() => {
    if (!open || row?.linkedModuleId == null) {
      setLinkedInsuranceType(null);
      return undefined;
    }
    let cancelled = false;
    axios
      .get(`${API_URL}/insurance/${row.linkedModuleId}`)
      .then((response) => {
        if (!cancelled) setLinkedInsuranceType(response.data?.data?.type || null);
      })
      .catch(() => {
        if (!cancelled) setLinkedInsuranceType(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, row?.linkedModuleId]);

  const onFailure = (error) =>
    toast.error(
      error?.response?.data?.errorMessage ||
        (oneTime ? "Could not save this commission" : "Could not save this month of commission")
    );

  const mutationOptions = { onAccountChanged, onError: onFailure };
  const createMonth = useCreateCommissionMonth(accountDetailId, mutationOptions);
  const updateMonth = useUpdateCommissionMonth(accountDetailId, mutationOptions);
  const deleteMonth = useDeleteCommissionMonth(accountDetailId, {
    ...mutationOptions,
    onError: (error) =>
      toast.error(
        error?.response?.data?.errorMessage ||
          (oneTime ? "Could not delete this commission" : "Could not delete this month")
      ),
  });

  // Server state is the ledger; local rows only ever add drafts and pending edits on top of it.
  useEffect(() => {
    if (!open) return;
    setRows((current) => {
      const drafts = current.filter((item) => !item.id);
      // A refetch triggered by saving one row must not throw away edits typed into another.
      const pendingEdits = new Map(
        current.filter((item) => item.id && item.dirty).map((item) => [item.id, item])
      );
      const recorded = (ledger?.months || []).map(
        (month) => pendingEdits.get(month.id) || toEditableRow(month)
      );
      return [...recorded, ...drafts];
    });
    setErrors({});
  }, [open, ledger]);

  useEffect(() => {
    if (!open) {
      setRows([]);
      setNotesTarget(null);
      setDeleteTarget(null);
    }
  }, [open]);

  const totals = useMemo(
    () => ({
      count: ledger?.months?.length || 0,
      total: Number(ledger?.totalReceived || 0),
      last: [...(ledger?.months || [])].at(-1) || null,
    }),
    [ledger]
  );

  const canAddRow = !locked && (!oneTime || rows.length === 0);

  const patchRow = (key, patch) => {
    setRows((current) =>
      current.map((item) => (item.key === key ? { ...item, ...patch, dirty: true } : item))
    );
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const addDraft = () => {
    if (!canAddRow) return;
    setRows((current) => [
      ...current,
      {
        key: draftKey(),
        month: oneTime ? null : nextMonthAfter(current.map((item) => item.month)),
        amount: "",
        receivedDate: todayInput(),
        notesCount: 0,
        dirty: true,
      },
    ]);
  };

  const confirmRow = async (item) => {
    const rowErrors = validate(item, rows, { oneTime });
    if (Object.keys(rowErrors).length) {
      setErrors((current) => ({ ...current, [item.key]: rowErrors }));
      return;
    }
    // Same commission-months payload; OSHC one-time sends month/forMonth as null.
    const forMonth = oneTime ? null : item.month || null;
    const payload = {
      month: forMonth,
      forMonth,
      amount: String(item.amount),
      receivedDate: item.receivedDate || null,
    };
    if (item.id) {
      await updateMonth.mutateAsync({ commissionMonthId: item.id, ...payload });
      toast.success(oneTime ? "Commission updated" : "Commission month updated");
    } else {
      await createMonth.mutateAsync(payload);
      setRows((current) => current.filter((existing) => existing.key !== item.key));
      toast.success(oneTime ? "Commission recorded" : "Commission month recorded");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    await deleteMonth.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
    toast.success(oneTime ? "Commission deleted" : "Commission month deleted");
  };

  const saving = createMonth.isLoading || updateMonth.isLoading;
  const unsaved = rows.some((item) => item.dirty || !item.id);

  const handleClose = () => {
    if (
      unsaved &&
      !window.confirm(
        oneTime
          ? "Discard the commission you have not recorded yet?"
          : "Discard the month you have not recorded yet?"
      )
    ) {
      return;
    }
    onClose?.();
  };

  const colSpan = oneTime ? 3 : 4;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Commission received
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
            {row?.linkedModuleSummary || row?.module || "Insurance"}
            {oneTime ? " · OSHC (one-time)" : null}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {oneTime
              ? "OSHC commission is recorded once when it arrives from the insurer. It counts toward profit."
              : "Record commission when it arrives from the insurer. Each month is one received amount and counts toward profit."}
          </Typography>

          {locked && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Commission is marked complete for this record. Reopen it on the record's card to
              {oneTime ? " change it." : " change any month."}
            </Alert>
          )}

          <Box className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {oneTime ? "Recorded" : "Months recorded"}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700}>
                {oneTime ? (totals.count > 0 ? "Yes" : "No") : totals.count}
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Total received
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} color="success.main">
                {money(totals.total)}
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {oneTime ? "Received on" : "Last received"}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700}>
                {totals.last?.receivedDate ? formatDisplayDate(totals.last.receivedDate) : "—"}
              </Typography>
            </Paper>
          </Box>

          {ledgerQuery.isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  {!oneTime && <TableCell>For month</TableCell>}
                  <TableCell>Amount</TableCell>
                  <TableCell>Received on</TableCell>
                  <TableCell align="right">Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((item) => (
                  <CommissionMonthRow
                    key={item.key}
                    row={item}
                    oneTime={oneTime}
                    errors={errors[item.key] || {}}
                    disabled={locked}
                    saving={saving}
                    maxDate={todayInput()}
                    onChange={(patch) => patchRow(item.key, patch)}
                    onConfirm={() => confirmRow(item)}
                    onOpenNotes={() => setNotesTarget(item)}
                    onDelete={() => setDeleteTarget(item)}
                  />
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={colSpan}>
                      <Typography variant="body2" color="text.secondary">
                        No commission recorded yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}

          <Button
            startIcon={<AddCircleOutline />}
            onClick={addDraft}
            disabled={!canAddRow}
            sx={{ mt: 2 }}
          >
            {oneTime ? "Add commission" : "Add month"}
          </Button>
          {locked && (
            <Chip size="small" color="success" label="Commission complete" sx={{ ml: 2, mt: 2 }} />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        title={oneTime ? "Delete commission" : "Delete commission month"}
        message={
          <>
            {oneTime
              ? `Delete this commission of ${money(deleteTarget?.amount)}?`
              : `Delete the ${deleteTarget?.month || "selected"} commission of ${money(
                  deleteTarget?.amount
                )}?`}
            {Number(deleteTarget?.notesCount || 0) > 0 && (
              <>
                {" "}
                Its {deleteTarget.notesCount} note
                {Number(deleteTarget.notesCount) === 1 ? "" : "s"} and any attachments will be
                deleted with it.
              </>
            )}{" "}
            This cannot be undone.
          </>
        }
        loading={deleteMonth.isLoading}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {notesTarget?.id && (
        <NotesModal
          open={Boolean(notesTarget)}
          handleClose={() => setNotesTarget(null)}
          standalone
          standaloneParentType="COMMISSION_MONTH"
          standaloneParentId={notesTarget.id}
          initialNotes={notesQuery.data || []}
          onChanged={() => {
            notesQuery.refetch();
            ledgerQuery.refetch();
          }}
        />
      )}
    </>
  );
};

export default InsuranceCommissionModal;
